import React from 'react';
import type { LoginNodeData, PageNodeData, ActionNodeData, AssertionNodeData, SetupNodeData, SeedNodeData, WaitNodeData, ConditionNodeData, ApiSequenceNodeData, SandboxActionNodeData, McpNodeData, NotificationNodeData, AudioNodeData } from '@chatsdet/ui';
import { isTempNodeId } from './utils';
import type { WorkflowNode } from '../../../types/workflow';

export type WorkflowNodeType =
  | 'login'
  | 'navigate'
  | 'action'
  | 'assertion'
  | 'setup'
  | 'seed'
  | 'wait'
  | 'condition'
  | 'api_sequence'
  | 'sandbox_action'
  | 'mcp'
  | 'notification'
  | 'audio'
  | 'audio-assertion'
  | 'visual_checkpoint'
  | 'end';
export type QuickCreateTarget =
  | 'navigate'
  | 'action'
  | 'assertion'
  | 'wait'
  | 'login'
  | 'condition'
  | 'api_sequence'
  | 'sandbox_action'
  | 'mcp'
  | 'seed'
  | 'notification'
  | 'audio'
  | 'audio-assertion'
  | 'visual_checkpoint'
  | 'end';
export type BranchType = 'condition_true' | 'condition_false';

type SharedNodeQuickHandlers = Partial<
  Pick<
    LoginNodeData,
    | 'onCreateNavigate'
    | 'onCreateAction'
    | 'onCreateAssertion'
    | 'onCreateWait'
    | 'onCreateCondition'
  >
> &
  Partial<
    Pick<
      AssertionNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > &
  Partial<
    Pick<
      ActionNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > &
  Partial<
    Pick<
      PageNodeData,
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > &
  Partial<
    Pick<
      SetupNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > &
  Partial<
    Pick<
      WaitNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > &
  Partial<
    Pick<
      ConditionNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
      | 'onCreateYesBranchNode'
      | 'onCreateNoBranchNode'
    >
  > &
  Partial<
    Pick<
      ApiSequenceNodeData,
      | 'onCreateNavigate'
      | 'onCreateAction'
      | 'onCreateAssertion'
      | 'onCreateWait'
      | 'onCreateLogin'
      | 'onCreateCondition'
    >
  > & {
    onCreateApiSequence?: () => void;
    onCreateSandboxAction?: () => void;
    onCreateMcp?: () => void;
    onCreateNotification?: () => void;
    onCreateAudio?: () => void;
    onCreateAudioAssertion?: () => void;
    onCreateVisualCheckpoint?: () => void;
    onCreateEnd?: () => void;
  };

export function buildNodeQuickCreateHandlers({
  nodeType,
  nodeId,
  allowTemp = false,
  handleQuickCreate,
  handleBranchCreate,
  enableApiSequence = false,
  enableSandboxAction = false,
  enableMcp = false,
}: {
  nodeType: WorkflowNodeType;
  nodeId: string;
  allowTemp?: boolean;
  handleQuickCreate: (sourceNodeId: string, nextType: QuickCreateTarget) => void;
  /** Handler for creating nodes on condition branches with specific edge types */
  handleBranchCreate?: (
    sourceNodeId: string,
    nodeType: QuickCreateTarget,
    branchType: BranchType
  ) => void;
  /** Whether to include API Sequence in quick create options (requires feature flag) */
  enableApiSequence?: boolean;
  /** Whether to include Sandbox Action (requires a verified sandbox template with actions) */
  enableSandboxAction?: boolean;
  /** Whether to include MCP (requires the mcpTesting feature) */
  enableMcp?: boolean;
}): SharedNodeQuickHandlers {
  const buildHandler =
    <T>(nextType: QuickCreateTarget) =>
    (_data: T) => {
      if (!nodeId) {
        return;
      }
      if (!allowTemp && isTempNodeId(nodeId)) {
        return;
      }
      handleQuickCreate(nodeId, nextType);
    };

  const apiSequenceHandler = enableApiSequence
    ? {
        onCreateApiSequence: () => {
          if (!nodeId) return;
          if (!allowTemp && isTempNodeId(nodeId)) return;
          handleQuickCreate(nodeId, 'api_sequence');
        },
      }
    : {};

  const sandboxActionHandler = enableSandboxAction
    ? {
        onCreateSandboxAction: () => {
          if (!nodeId) return;
          if (!allowTemp && isTempNodeId(nodeId)) return;
          handleQuickCreate(nodeId, 'sandbox_action');
        },
      }
    : {};

  const mcpHandler = enableMcp
    ? {
        onCreateMcp: () => {
          if (!nodeId) return;
          if (!allowTemp && isTempNodeId(nodeId)) return;
          handleQuickCreate(nodeId, 'mcp');
        },
      }
    : {};

  const endHandler = {
    onCreateEnd: () => {
      if (!nodeId) return;
      if (!allowTemp && isTempNodeId(nodeId)) return;
      handleQuickCreate(nodeId, 'end');
    },
  };

  const notificationHandler = {
    onCreateNotification: () => {
      if (!nodeId) return;
      if (!allowTemp && isTempNodeId(nodeId)) return;
      handleQuickCreate(nodeId, 'notification');
    },
  };

  const audioHandler = {
    onCreateAudio: () => {
      if (!nodeId) return;
      if (!allowTemp && isTempNodeId(nodeId)) return;
      handleQuickCreate(nodeId, 'audio');
    },
  };

  const audioAssertionHandler = {
    onCreateAudioAssertion: () => {
      if (!nodeId) return;
      if (!allowTemp && isTempNodeId(nodeId)) return;
      handleQuickCreate(nodeId, 'audio-assertion');
    },
  };

  const visualCheckpointHandler = {
    onCreateVisualCheckpoint: () => {
      if (!nodeId) return;
      if (!allowTemp && isTempNodeId(nodeId)) return;
      handleQuickCreate(nodeId, 'visual_checkpoint');
    },
  };

  switch (nodeType) {
    case 'login':
      return {
        onCreateNavigate: buildHandler<LoginNodeData>('navigate'),
        onCreateAction: buildHandler<LoginNodeData>('action'),
        onCreateAssertion: buildHandler<LoginNodeData>('assertion'),
        onCreateWait: buildHandler<LoginNodeData>('wait'),
        onCreateCondition: buildHandler<LoginNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'navigate':
      return {
        onCreateAction: buildHandler<PageNodeData>('action'),
        onCreateAssertion: buildHandler<PageNodeData>('assertion'),
        onCreateWait: buildHandler<PageNodeData>('wait'),
        onCreateLogin: buildHandler<PageNodeData>('login'),
        onCreateCondition: buildHandler<PageNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'action':
      return {
        onCreateNavigate: buildHandler<ActionNodeData>('navigate'),
        onCreateAction: buildHandler<ActionNodeData>('action'),
        onCreateAssertion: buildHandler<ActionNodeData>('assertion'),
        onCreateWait: buildHandler<ActionNodeData>('wait'),
        onCreateLogin: buildHandler<ActionNodeData>('login'),
        onCreateCondition: buildHandler<ActionNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'assertion':
      return {
        onCreateNavigate: buildHandler<AssertionNodeData>('navigate'),
        onCreateAction: buildHandler<AssertionNodeData>('action'),
        onCreateAssertion: buildHandler<AssertionNodeData>('assertion'),
        onCreateWait: buildHandler<AssertionNodeData>('wait'),
        onCreateLogin: buildHandler<AssertionNodeData>('login'),
        onCreateCondition: buildHandler<AssertionNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'setup':
      return {
        onCreateNavigate: buildHandler<SetupNodeData>('navigate'),
        onCreateAction: buildHandler<SetupNodeData>('action'),
        onCreateAssertion: buildHandler<SetupNodeData>('assertion'),
        onCreateWait: buildHandler<SetupNodeData>('wait'),
        onCreateLogin: buildHandler<SetupNodeData>('login'),
        onCreateCondition: buildHandler<SetupNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'seed':
      return {
        onCreateNavigate: buildHandler<SeedNodeData>('navigate'),
        onCreateAction: buildHandler<SeedNodeData>('action'),
        onCreateAssertion: buildHandler<SeedNodeData>('assertion'),
        onCreateWait: buildHandler<SeedNodeData>('wait'),
        onCreateLogin: buildHandler<SeedNodeData>('login'),
        onCreateCondition: buildHandler<SeedNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'wait':
      return {
        onCreateNavigate: buildHandler<WaitNodeData>('navigate'),
        onCreateAction: buildHandler<WaitNodeData>('action'),
        onCreateAssertion: buildHandler<WaitNodeData>('assertion'),
        onCreateLogin: buildHandler<WaitNodeData>('login'),
        onCreateCondition: buildHandler<WaitNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'condition': {
      // Build per-handle branch handlers for condition nodes
      const buildBranchHandler = (branchType: BranchType) => (nextNodeType: string) => {
        if (!nodeId) return;
        if (!allowTemp && isTempNodeId(nodeId)) return;
        if (!handleBranchCreate) return;
        handleBranchCreate(nodeId, nextNodeType as QuickCreateTarget, branchType);
      };

      return {
        onCreateNavigate: buildHandler<ConditionNodeData>('navigate'),
        onCreateAction: buildHandler<ConditionNodeData>('action'),
        onCreateAssertion: buildHandler<ConditionNodeData>('assertion'),
        onCreateWait: buildHandler<ConditionNodeData>('wait'),
        onCreateLogin: buildHandler<ConditionNodeData>('login'),
        onCreateCondition: buildHandler<ConditionNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
        // Per-handle branch creation
        onCreateYesBranchNode: handleBranchCreate
          ? buildBranchHandler('condition_true')
          : undefined,
        onCreateNoBranchNode: handleBranchCreate
          ? buildBranchHandler('condition_false')
          : undefined,
      };
    }
    case 'api_sequence':
      return {
        onCreateNavigate: buildHandler<ApiSequenceNodeData>('navigate'),
        onCreateAction: buildHandler<ApiSequenceNodeData>('action'),
        onCreateAssertion: buildHandler<ApiSequenceNodeData>('assertion'),
        onCreateWait: buildHandler<ApiSequenceNodeData>('wait'),
        onCreateLogin: buildHandler<ApiSequenceNodeData>('login'),
        onCreateCondition: buildHandler<ApiSequenceNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'sandbox_action':
      return {
        onCreateNavigate: buildHandler<SandboxActionNodeData>('navigate'),
        onCreateAction: buildHandler<SandboxActionNodeData>('action'),
        onCreateAssertion: buildHandler<SandboxActionNodeData>('assertion'),
        onCreateWait: buildHandler<SandboxActionNodeData>('wait'),
        onCreateLogin: buildHandler<SandboxActionNodeData>('login'),
        onCreateCondition: buildHandler<SandboxActionNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'mcp':
      return {
        onCreateNavigate: buildHandler<McpNodeData>('navigate'),
        onCreateAction: buildHandler<McpNodeData>('action'),
        onCreateAssertion: buildHandler<McpNodeData>('assertion'),
        onCreateWait: buildHandler<McpNodeData>('wait'),
        onCreateLogin: buildHandler<McpNodeData>('login'),
        onCreateCondition: buildHandler<McpNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'notification':
      return {
        onCreateNavigate: buildHandler<NotificationNodeData>('navigate'),
        onCreateAction: buildHandler<NotificationNodeData>('action'),
        onCreateAssertion: buildHandler<NotificationNodeData>('assertion'),
        onCreateWait: buildHandler<NotificationNodeData>('wait'),
        onCreateCondition: buildHandler<NotificationNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'audio':
      return {
        onCreateNavigate: buildHandler<AudioNodeData>('navigate'),
        onCreateAction: buildHandler<AudioNodeData>('action'),
        onCreateAssertion: buildHandler<AudioNodeData>('assertion'),
        onCreateWait: buildHandler<AudioNodeData>('wait'),
        onCreateCondition: buildHandler<AudioNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'audio-assertion':
    case 'visual_checkpoint':
      return {
        onCreateNavigate: buildHandler<AudioNodeData>('navigate'),
        onCreateAction: buildHandler<AudioNodeData>('action'),
        onCreateAssertion: buildHandler<AudioNodeData>('assertion'),
        onCreateWait: buildHandler<AudioNodeData>('wait'),
        onCreateCondition: buildHandler<AudioNodeData>('condition'),
        ...apiSequenceHandler,
        ...sandboxActionHandler,
        ...mcpHandler,
        ...notificationHandler,
        ...audioHandler,
        ...audioAssertionHandler,
        ...visualCheckpointHandler,
        ...endHandler,
      };
    case 'end':
      // Terminal node - no quick create actions
      return {};
    default:
      return {};
  }
}

export function useStableNodesState() {
  const [nodes, setNodes] = React.useState<WorkflowNode[]>([]);
  const nodesRef = React.useRef<WorkflowNode[]>([]);

  React.useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const getNodes = React.useCallback(() => nodesRef.current, []);

  return { nodes, setNodes, getNodes };
}

export function useWorkflowHydrationGuard() {
  const ref = React.useRef<{ workflowId: string | null; version: number | string | null }>({
    workflowId: null,
    version: null,
  });

  const shouldHydrate = React.useCallback(
    (workflowId: string | null, version: number | string | null) => {
      if (!workflowId) {
        return false;
      }
      const sameWorkflow = ref.current.workflowId === workflowId;
      const sameVersion = ref.current.version === version;
      if (sameWorkflow && sameVersion) {
        return false;
      }
      ref.current = { workflowId, version };
      return true;
    },
    []
  );

  const resetHydration = React.useCallback(() => {
    ref.current = { workflowId: null, version: null };
  }, []);

  return { shouldHydrate, resetHydration };
}
